import { useState, useEffect } from "react";
import { Button } from "../ui/Button.tsx";
import { Input } from "../ui/Input.tsx";
import { Label } from "../ui/Label.tsx";
import { Mail, Send } from "lucide-react";
import { toast } from "react-toastify";
import { getSmtpConfig, saveSmtpConfig, sendTestEmail } from "../../apiWithSmtp.js";

export default function SmtpConfiguration() { 
  const [formData, setFormData] = useState({
    host: "",
    port: "",
    username: "",
    password: "",
    fromEmail: "",
    fromName: "",
    use_tls: true,
    use_ssl: false,
  });

  // Add password state for test email form
  const [testEmail, setTestEmail] = useState("");
  const [testEmailPassword, setTestEmailPassword] = useState("");

  useEffect(() => {
  const fetchSmtpConfig = async () => {
    try {
      const { data } = await getSmtpConfig();

      if (data) {
        const configToSet = {
          host: data.host || "",
          port: data.port?.toString() || "",
          username: data.username || "",
          fromEmail: data.fromEmail || "",
          fromName: data.fromName || "",
          use_tls: data.use_tls ?? true,
          use_ssl: data.use_ssl ?? false,
          password: "",
        };

        setFormData(configToSet);
        toast.success("SMTP configuration loaded successfully");
      }
    } catch (error) {
      console.warn("No SMTP config found. Showing default placeholders.");
      toast.warn("SMTP configuration not available. Please enter SMTP details.");
    }
  };
  fetchSmtpConfig();
}, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const portNumber = Number(formData.port);
    const portToSend = portNumber > 0 ? portNumber : 587;

    const payload = {
      host: formData.host.trim(),
      port: portToSend,
      username: formData.username.trim(),
      password: formData.password,
      fromEmail: formData.fromEmail.trim(),
      fromName: formData.fromName.trim(),
      use_tls: formData.use_tls,
      use_ssl: formData.use_ssl,
    };

    try {
      const { data } = await saveSmtpConfig(payload);
      setFormData((prev) => ({
        ...prev,
        host: payload.host,
        port: String(payload.port),
        username: payload.username,
        fromEmail: payload.fromEmail,
        fromName: payload.fromName,
        use_tls: payload.use_tls,
        use_ssl: payload.use_ssl,
        password: "",
      }));
      toast.success(data.message ?? "SMTP configuration saved successfully");
    } catch (error) {
      const msg =
        error.response?.data?.error ??
        error.response?.data?.message ??
        error.message ??
        "Network error while saving SMTP configuration";
      toast.error(msg);
      console.error("SMTP Save Error:", error);
    }
  };

  const handleTestEmail = async (e) => {
    e.preventDefault();

    if (!testEmail) {
      toast.error("Please enter an email address");
      return;
    }

    if (!testEmailPassword) {
      toast.error("Please enter the SMTP password");
      return;
    }

    const payload = {
      recipient_email: testEmail.trim(),
      password: testEmailPassword,  
    };

    try {
      const response = await sendTestEmail(payload);
      toast.success(response.data.message || `Test email sent to ${testEmail}`);
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.message ||
        "Network error while sending test email";
      toast.error(msg);
      console.error("Test Email Error:", error);
    }
  };

 return (
<div className="w-full h-full min-h-0 flex flex-col">

    {/* ===== HEADER ===== */}
    <div>
      <h1 className="text-xl font-semibold flex items-center gap-2">
        <Mail className="w-5 h-5 text-primary" />
        SMTP Configuration
      </h1>
      <p className="text-sm text-muted-foreground mt-1">
        Configure email server settings for system notifications
      </p>
    </div>
  <div className="flex-1 min-h-0 overflow-y-auto space-y-6 pr-1">

    {/* ===== SMTP SETTINGS ===== */}
   <div className="flex-1 min-h-0 overflow-y-auto space-y-6">
   <div className="border border-border rounded-lg bg-background">
      <form
        onSubmit={handleSubmit}
        className="p-6 space-y-6"
      >

        <h3 className="text-sm font-semibold text-muted-foreground">
          Server Configuration
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>SMTP Host</Label>
            <Input
              value={formData.host}
              onChange={(e) =>
                setFormData({ ...formData, host: e.target.value })
              }
              placeholder="smtp.example.com"
              required
            />
          </div>

          <div>
            <Label>Port</Label>
            <Input
              type="number"
              value={formData.port}
              onChange={(e) =>
                setFormData({ ...formData, port: e.target.value })
              }
              placeholder="587"
              required
            />
          </div>

          <div>
            <Label>Username</Label>
            <Input
              type="email"
              value={formData.username}
              onChange={(e) =>
                setFormData({ ...formData, username: e.target.value })
              }
              placeholder="admin@example.com"
              required
            />
          </div>

          <div>
            <Label>Password</Label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              placeholder="Enter password"
            />
          </div>

          <div>
            <Label>From Email</Label>
            <Input
              type="email"
              value={formData.fromEmail}
              onChange={(e) =>
                setFormData({ ...formData, fromEmail: e.target.value })
              }
              placeholder="noreply@example.com"
              required
            />
          </div>

          <div>
            <Label>From Name</Label>
            <Input
              value={formData.fromName}
              onChange={(e) =>
                setFormData({ ...formData, fromName: e.target.value })
              }
              placeholder="Admin Panel"
              required
            />
          </div>
        </div>

        {/* ===== ENCRYPTION ===== */}
        <div className="space-y-2">
          <Label>Encryption</Label>
          <div className="flex flex-wrap gap-4 text-sm">
            {[
              { label: "TLS", tls: true, ssl: false },
              { label: "SSL", tls: false, ssl: true },
              { label: "None", tls: false, ssl: false }
            ].map(opt => (
              <label key={opt.label} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="encryption"
                  checked={
                    formData.use_tls === opt.tls &&
                    formData.use_ssl === opt.ssl
                  }
                  onChange={() =>
                    setFormData({
                      ...formData,
                      use_tls: opt.tls,
                      use_ssl: opt.ssl
                    })
                  }
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t border-border">
          <Button type="submit" size="sm">
            <Mail className="h-4 w-4 mr-2" />
            Save Configuration
          </Button>
        </div>
      </form>
    </div>
    </div>

    {/* ===== TEST EMAIL ===== */}
    <div className="border border-border rounded-lg bg-background">
      <form
        onSubmit={handleTestEmail}
        className="p-6 space-y-6"
      >
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground">
            Test Email
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Verify SMTP configuration by sending a test email
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Recipient Email</Label>
            <Input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="recipient@example.com"
              required
            />
          </div>

          <div>
            <Label>SMTP Password</Label>
            <Input
              type="password"
              value={testEmailPassword}
              onChange={(e) => setTestEmailPassword(e.target.value)}
              placeholder="Enter SMTP password"
              required
            />
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t border-border">
          <Button type="submit" size="sm" variant="secondary">
            <Send className="h-4 w-4 mr-2" />
            Send Test Email
          </Button>
        </div>
      </form>
    </div>
  </div>
    </div>
);

}
